import AddIcon from "@mui/icons-material/Add";
import {
    Box,
    Button,
    Card,
    CardContent,
    CardMedia,
    Grid,
    Paper,
    PaperProps,
    Stack,
    Tab,
    Tabs,
    Typography
} from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import api from "../../../../api";
import PlaceHolderImage from "../../../../components/svg/PlaceHolderImage";
import { useLogin } from "../../../../context/Login/LoginContext";
import { getBackendUrl, getServerImage } from "../../../../util/envFn";
import { useUnsavedChangesWarning } from "../../hooks/useUnsavedChangesWarning";
import { Portfolio } from "./types";
export type PortfolioCardProps = {
    userId: string;
    editMode: boolean;
    paperProps?: PaperProps;
    disabledEdit?: boolean;
    hideDraft?: boolean;
}

const PortfolioCard: React.FC<PortfolioCardProps> = ({ userId, editMode, paperProps, disabledEdit, hideDraft }) => {
    const [portfolioTab, setPortfolioTab] = useState<number>(0);
    const [projects, setProjects] = useState<Portfolio[]>([]);
    const { user } = useLogin();
    const { enqueueSnackbar } = useSnackbar();
    const { confirmNavigate } = useUnsavedChangesWarning(editMode);

    const draftProjects = projects.filter((project) => project.isDraft);
    const publishedProjects = projects.filter((project) => !project.isDraft);
    const displayProjects = portfolioTab === 0 ? publishedProjects : draftProjects;

    useEffect(() => {
        fetchPortfolio();
    }, [userId]);

    const fetchPortfolio = () => {
        api.get(getBackendUrl(`/user-portfolio/user/${userId}`))
            .then(res => {
                setProjects(res.data ?? []);
            })
            .catch(err => {
                console.error(err);
            });
    }

    const handleAddProject = () => {
        api.post(getBackendUrl(`/user-portfolio`), {
            title: "New Project",
            description: "New Project Description",
            image: "https://via.placeholder.com/150",
        })
            .then(res => {
                setPortfolioTab(1);
                fetchPortfolio();
                enqueueSnackbar("Project added successfully", { variant: "success" });
            })
            .catch(err => {
                console.error(err);
                enqueueSnackbar("Failed to add project", { variant: "error" });
            });
    }

    return (
        <Paper {...paperProps} sx={{ padding: 2, mb: 3, borderRadius: 2, ...paperProps?.sx }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h6">Portfolio</Typography>
                {editMode && !disabledEdit && (
                    <Button startIcon={<AddIcon />} variant="outlined" size="small">
                        Add Project
                    </Button>
                )}
            </Box>

            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                {
                    !hideDraft && (
                        <Tabs
                            value={portfolioTab}
                            onChange={(e: React.SyntheticEvent, newValue: number) => setPortfolioTab(newValue)}
                            sx={{ mb: 2 }}
                        >
                            <Tab label="Published" />
                            <Tab label="Drafts" />
                        </Tabs>
                    )
                }
                {
                    userId === user?._id && !disabledEdit && (
                        <Button variant="outlined" size="small" onClick={handleAddProject}>
                            Add Project
                        </Button>
                    )
                }
            </Stack>

            <Grid container spacing={2}>
                {
                    displayProjects.length === 0 ?
                        (
                            <Stack minHeight={200} flex={1} justifyContent="center" alignItems="center">
                                <Typography align="center" color="text.secondary">
                                    No portfolio items yet
                                </Typography>
                            </Stack>
                        ) :
                        (
                            displayProjects.map((project, index) => (
                                <PortfolioCardItem key={index} project={project} confirmNavigate={confirmNavigate} />
                            ))
                        )
                }
            </Grid>
        </Paper>
    );
};

type PortfolioCardItemProps = {
    project: Portfolio;
    confirmNavigate: (path: string) => void;
}

const PortfolioCardItem = ({ project, confirmNavigate }: PortfolioCardItemProps) => {
    const [isImageFailed, setIsImageFailed] = useState(false);
    const handleImageError = (project: Portfolio) => setIsImageFailed(true);

    return (
        <Grid item xs={12} sm={6}
            onClick={() => confirmNavigate(`/portfolio/${project._id}`)}
            sx={{
                cursor: "pointer",
                "&:hover": {
                    transform: "scale(1.05)",
                    transition: "transform 0.3s ease-in-out"
                }
            }}
        >
            <Card>
                {
                    project.visualContent && !isImageFailed ? (
                        <CardMedia
                            component="img"
                            height="140"
                            width="200"
                            image={getServerImage(project.visualContent)}
                            alt={project.title}
                            onError={() => handleImageError(project)}
                        />
                    ) : (
                        <PlaceHolderImage height={160} width={1} />
                    )
                }
                <CardContent>
                    <Typography variant="subtitle1" noWrap>
                        {project.title}
                    </Typography>
                </CardContent>
            </Card>
        </Grid>

    )
}

export default PortfolioCard; 